import React from 'react';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import { DataTable, toast, Button as DsButton, PageTitle, Panel } from '../../../components/ds';
import AddIcon from '@material-ui/icons/Add';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogActions from '@material-ui/core/DialogActions';
import Icon from '@material-ui/core/Icon';
import DeleteIcon from '@material-ui/icons/DeleteForever';
import EditICon from '@material-ui/icons/Edit';
import { FormControl, Grid, Checkbox } from '@material-ui/core';
import { browserHistory } from 'react-router';
import ReactExport from 'react-data-export';
import readXlsxFile from 'read-excel-file';
import * as XLSX from 'xlsx';
import InputLabel from '@material-ui/core/InputLabel';
import TextField from '@material-ui/core/TextField';

import Form from './Form';
import AppState from '../../../state';
import { Utils } from '../../../services';
import Modal from '../../../components/lib/Modal';
import ConfirmDeleteForm from './ConfirmDeleteForm';
import * as lancamentoAPi from '../../../api/lancamentos';
import debounce from 'lodash.debounce';

const ExcelFile = ReactExport.ExcelFile;
const ExcelSheet = ReactExport.ExcelFile.ExcelSheet;
const ExcelColumn = ReactExport.ExcelFile.ExcelColumn;

const schema = {
  'CPF Funcionário': {
    prop: 'cpf',
    type: String,
    required: false,
    // Excel stores dates as integers.
    // E.g. '24/03/2018' === 43183.
    // Such dates are parsed to UTC+0 timezone with time 12:00 .
  },
  'Conta Contábil': {
    prop: 'conta',
    type: String,
    required: false,
  },
  'É corrente?': {
    prop: 'eh_corrente',
    type: String,
    required: false,
    parse(value) {
      return value === 'Sim' ? 'true' : value === 'Não' ? 'false' : '';
    },
    // Excel stored booleans as numbers:
    // `1` is `true` and `0` is `false`.
    // Such numbers are parsed to booleans.
  },
  Valor: {
    prop: 'valor',
    type: Number,
    required: false,
  },
  Descrição: {
    prop: 'desc',
    type: String,
    required: false,
  },
  'Nº Banco de Pagamento': {
    prop: 'n_banco',
    type: String,
    required: false,
  },
  'Dia Pgto': {
    prop: 'dt_pagamento',
    type: Date,
    required: false,
  },
  'Data Referência': {
    prop: 'dt_referencia',
    type: Date,
    required: false,
  },
  Referência: {
    prop: 'referencia',
    type: String,
    required: false,
    parse(value) {
      return value ? String(value).trim() : '';
    },
  },
  // Colunas novas: só fazem sentido para algumas contas, então entram como
  // opcionais aqui e a obrigatoriedade é checada depois, olhando a conta da
  // linha (ver CONTAS_FERIAS / CONTA_DECIMO abaixo). O read-excel-file não tem
  // como expressar "obrigatório quando a coluna X for tal valor".
  'Período Aquisitivo Início': {
    prop: 'periodo_aquisitivo_inicio',
    type: Date,
    required: false,
  },
  'Período Aquisitivo Fim': {
    prop: 'periodo_aquisitivo_fim',
    type: Date,
    required: false,
  },
  'Dias Gozados': {
    prop: 'dias_gozados',
    type: Number,
    required: false,
  },
  'Dias Vendidos': {
    prop: 'dias_vendidos',
    type: Number,
    required: false,
  },
  Parcela: {
    prop: 'parcela',
    type: String,
    required: false,
    parse(value) {
      return value ? String(value).trim() : '';
    },
  },
};

// Contas que exigem as colunas novas. Férias vem em duas linhas — a própria
// férias e o terço constitucional — e as duas descrevem o mesmo período
// aquisitivo, por isso as duas precisam trazer os mesmos campos.
const CONTA_FERIAS = '2.1.4.1';
const CONTA_TERCO_FERIAS = '2.1.4.2';
const CONTAS_FERIAS = [CONTA_FERIAS, CONTA_TERCO_FERIAS];
const CONTA_DECIMO = '2.1.4.4';
const PARCELAS_DECIMO = ['Adiantamento', 'Saldo', 'Rescisório'];

// Colunas que a importação espera encontrar no cabeçalho. Serve para dizer o
// nome exato da que faltar, em vez de importar torto — o modelo é um arquivo
// mantido à mão em server/arquivos, então ele pode ficar para trás do código.
//
// 'Data Referência' não entra: saiu do modelo novo e só é aceita por
// compatibilidade com as planilhas antigas.
const COLUNAS_ESPERADAS = [
  'CPF Funcionário',
  'Conta Contábil',
  'É corrente?',
  'Valor',
  'Descrição',
  'Nº Banco de Pagamento',
  'Dia Pgto',
];

// Colunas que só algumas contas usam. Faltando, a planilha ainda importa —
// mas se alguma linha precisar delas, o erro avisa que o modelo está velho.
const COLUNAS_FERIAS = [
  'Período Aquisitivo Início',
  'Período Aquisitivo Fim',
  'Dias Gozados',
  'Dias Vendidos',
];
const COLUNA_PARCELA = 'Parcela';

// Comparação tolerante: a planilha é digitada à mão, então maiúscula, acento e
// espaço sobrando não podem ser motivo de recusa. O servidor já compara assim.
const normalizarTexto = (valor) =>
  String(valor === null || valor === undefined ? '' : valor)
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

const paraISO = (valor) => {
  if (!valor) return '';
  if (valor instanceof Date) {
    const mes = String(valor.getMonth() + 1).padStart(2, '0');
    const dia = String(valor.getDate()).padStart(2, '0');
    return `${valor.getFullYear()}-${mes}-${dia}`;
  }
  const texto = String(valor).trim();
  const br = texto.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (br) return `${br[3]}-${br[2]}-${br[1]}`;
  return texto;
};

// Célula em branco: o read-excel-file simplesmente não cria a propriedade, então
// chega undefined — e não null ou '' como a validação antiga esperava.
const vazio = (v) => v === null || v === undefined || String(v).trim() === '';

// Linha que tem qualquer coisa preenchida entra na validação. Antes só entrava
// quem tinha CPF: uma linha com valor e conta mas sem CPF era descartada em
// silêncio, e o resto da planilha importava sem avisar.
const CAMPOS_DA_LINHA = ['cpf', 'conta', 'eh_corrente', 'valor', 'desc', 'n_banco', 'dt_pagamento', 'referencia', 'dt_referencia'];
const linhaPreenchida = (entry) => CAMPOS_DA_LINHA.some((c) => !vazio(entry[c]));

class Pagamentos extends React.Component {
  constructor(props) {
    super(props);

    this.importInput = React.createRef();

    this.state = {
      dialog: false,
      dialogConfirmationDelete: false,
      lancamentoSelecionado: '',
      // Erros da importação do template, mostrados numa janela que só fecha
      // no clique — no aviso que some em 5 segundos não dava para ler a lista.
      errosImportacao: [],
      pagRebList: [],
      backupPagRebList: [],
      period: '5dias',
      employeeList: [],
      fullEmployeeList: [],
      dt_inicio: '',
      dt_fim: '',
      checked: [],
      selectAll: false,
      confirmation: false,
      confirmationMessage: '',
      statusFilter: 'Todos',
      periodType: 'dt_pag_recb',
      projectList: [],
      rateioFilter: false,
      custoDiretoFilter: false,
      naturezaFilter: 'Todos',
      tipoContraparteFilter: 'Todos',
      projetoFilter: ['all'],
      activeProjectList: [],
      ocultarContas4Filter: true,
    };

    this._debouncedHandleChange = debounce(this._debouncedHandleChange, 1000);
  }

  _debouncedHandleChange = () => {
    this.getTableData();
  };

  hasDeletePermission = () => {
    const permissions = JSON.parse(localStorage.getItem('Menu'));
    const lancamentoView = permissions.views
      .filter((view) => view.id_view === 'lancamento_pgto_receb')
      .pop();

    const hasDeletePermission = lancamentoView.funcionalidades.reduce(
      (acc, value) => {
        if (acc) return acc;
        return value.id_funcionalidade === 'delete_pgto_receb';
      },
      false
    );

    return hasDeletePermission;
  };

  hasEditPermission = () => {
    const permissions = JSON.parse(localStorage.getItem('Menu'));
    const lancamentoView = permissions.views
      .filter((view) => view.id_view === 'lancamento_pgto_receb')
      .pop();

    const hasEditPermission = lancamentoView.funcionalidades.reduce(
      (acc, value) => {
        if (acc) return acc;
        return value.id_funcionalidade === 'edit_pgto_receb';
      },
      false
    );

    return hasEditPermission;
  };

  componentDidMount() {
    this.getTableData();
    this.getUsuarios();
    this.getAllUsuarios();
    this.getActiveProjects();

    const input = document.getElementById('raised-button-file');
    let ref = this;
    input.addEventListener('change', () => {
      const parseReferenceDate = (value) => {
        if (value === null || value === undefined || value === '') return null;

        // Célula de data com formato personalizado (ex.: "mm/aaaa") chega aqui
        // como NÚMERO DE SÉRIE do Excel, não como data: a biblioteca decide se
        // a célula é data olhando o formato dela, e não reconhece formatos fora
        // da lista padrão. Era esse o caso de "08/2026", que na verdade guarda
        // 01/08/2026 e chegava como 46235.
        //
        // A faixa evita confundir com um ano digitado solto: 20000 é 1954-10-03
        // e 80000 é 2119-01-17.
        const comoNumero = Number(String(value).trim());
        if (
          Number.isFinite(comoNumero) &&
          comoNumero >= 20000 &&
          comoNumero <= 80000 &&
          /^\d+(\.\d+)?$/.test(String(value).trim())
        ) {
          const daSerie = new Date(
            Date.UTC(1899, 11, 30) + Math.floor(comoNumero) * 86400000
          );
          return daSerie.toISOString().slice(0, 10);
        }

        if (value instanceof Date && !Number.isNaN(value.getTime())) {
          const mes = String(value.getMonth() + 1).padStart(2, '0');
          const dia = String(value.getDate()).padStart(2, '0');
          return `${value.getFullYear()}-${mes}-${dia}`;
        }

        const normalizedValue = String(value).trim();
        if (!normalizedValue) return null;

        // Competência escrita como mês/ano vira o primeiro dia do mês.
        const mesAno = normalizedValue.match(/^(\d{2})[/-](\d{4})$/);
        if (mesAno) {
          const mes = parseInt(mesAno[1], 10);
          if (mes >= 1 && mes <= 12) {
            return `${mesAno[2]}-${mesAno[1]}-01`;
          }
          return null;
        }

        const referenceInEN = Utils.dateToEN(normalizedValue);

        if (!/^\d{4}-\d{2}-\d{2}$/.test(referenceInEN)) {
          return null;
        }

        const [year, month, day] = referenceInEN
          .split('-')
          .map((part) => parseInt(part, 10));
        const parsedDate = new Date(Date.UTC(year, month - 1, day));

        if (
          parsedDate.getUTCFullYear() !== year ||
          parsedDate.getUTCMonth() + 1 !== month ||
          parsedDate.getUTCDate() !== day
        ) {
          return null;
        }

        return referenceInEN;
      };

      // Leitura crua só do cabeçalho, antes da leitura com schema. O
      // read-excel-file ignora em silêncio coluna que não conhece e devolve
      // vazio para coluna que falta — então, sem esta conferência, uma planilha
      // desatualizada importa pela metade sem ninguém perceber.
      readXlsxFile(input.files[0]).then((linhasBrutas) => {
        const cabecalho = ((linhasBrutas && linhasBrutas[0]) || []).map((c) =>
          normalizarTexto(c)
        );
        const temColuna = (nome) => cabecalho.indexOf(normalizarTexto(nome)) >= 0;

        const faltando = COLUNAS_ESPERADAS.filter((c) => !temColuna(c));
        // Referência ou Data Referência: as duas alimentam o mesmo campo, e as
        // planilhas antigas só tinham a segunda.
        if (!temColuna('Referência') && !temColuna('Data Referência')) {
          faltando.push('Referência');
        }

        if (faltando.length > 0) {
          input.value = '';
          toast.error(
            'A planilha não tem a(s) coluna(s): ' +
              faltando.join(', ') +
              '. Baixe o modelo atualizado em "Baixar Template".'
          );
          return;
        }

        const temColunasFerias = COLUNAS_FERIAS.every(temColuna);
        const temColunaParcela = temColuna(COLUNA_PARCELA);

      readXlsxFile(input.files[0], { schema }).then(({ rows }) => {
        let errors = [];
        let ret = [];
        rows.forEach(function (entry, index) {
          if (linhaPreenchida(entry)) {
            if (vazio(entry.cpf)) {
              errors.push(
                "'CPF Funcionário' em branco na linha " + (index + 2)
              );
            } else if (
              !ref.state.fullEmployeeList.find(
                (x) => x.cpf_usuario === entry.cpf
              )
            ) {
              errors.push(
                'CPF ' + entry.cpf + ' não cadastrado no sistema na linha ' + (index + 2)
              );
            }
            if (vazio(entry.conta)) {
              errors.push(
                "'Conta Contábil' em branco na linha " + (index + 2)
              );
            }
            if (vazio(entry.eh_corrente)) {
              errors.push(
                "'É corrente?' em branco ou diferente de Sim/Não na linha " + (index + 2)
              );
            }
            if (vazio(entry.valor) || !entry.valor) {
              errors.push("'Valor' em branco, zerado ou inválido na linha " + (index + 2));
            }
            if (vazio(entry.desc)) {
              errors.push("'Descrição' em branco na linha " + (index + 2));
            }
            if (vazio(entry.n_banco)) {
              errors.push(
                "'Nº Banco de Pagamento' em branco na linha " + (index + 2)
              );
            }
            if (!entry.dt_pagamento) {
              errors.push(
                'Dia Pgto não informado na linha ' + (index + 2)
              );
            } else {
              const dtPgto = new Date(entry.dt_pagamento);
              const hoje = new Date();
              if (dtPgto.getMonth() !== hoje.getMonth() || dtPgto.getFullYear() !== hoje.getFullYear()) {
                errors.push(
                  'Dia Pgto deve estar dentro do mês corrente na linha ' + (index + 2)
                );
              }
            }
            const referenceFromString = parseReferenceDate(entry.referencia);
            const referenceDate = entry.dt_referencia
              ? new Date(entry.dt_referencia)
              : null;
            const referenceFromDate =
              referenceDate && !Number.isNaN(referenceDate.getTime())
                ? referenceDate.toISOString().slice(0, 10)
                : null;

            if (referenceFromString) {
              entry.referencia = referenceFromString;
            } else if (referenceFromDate) {
              entry.referencia = referenceFromDate;
            } else {
              errors.push(
                'Referência não informada ou inválida na linha ' +
                  (index + 2)
              );
            }
            // Obrigatoriedade condicional: estas colunas só existem por
            // causa de férias e 13º, e sem elas o lançamento entra mas a ficha
            // do funcionário fica pela metade — que é exatamente o retrabalho
            // que essa mudança existe para acabar.
            const conta = String(entry.conta || '').trim();

            if (CONTAS_FERIAS.indexOf(conta) >= 0 && !temColunasFerias) {
              errors.push(
                'A linha ' + (index + 2) +
                  ' é de férias, mas a planilha não tem as colunas de período aquisitivo. Baixe o modelo atualizado.'
              );
            } else if (CONTAS_FERIAS.indexOf(conta) >= 0) {
              const inicio = paraISO(entry.periodo_aquisitivo_inicio);
              const fim = paraISO(entry.periodo_aquisitivo_fim);

              if (!inicio || !fim) {
                errors.push(
                  'Conta ' + conta +
                    ' exige Período Aquisitivo Início e Fim na linha ' + (index + 2)
                );
              } else if (fim < inicio) {
                errors.push(
                  'Período aquisitivo com fim anterior ao início na linha ' + (index + 2)
                );
              } else {
                entry.periodo_aquisitivo_inicio = inicio;
                entry.periodo_aquisitivo_fim = fim;
              }

              if (entry.dias_gozados === null || entry.dias_gozados === undefined || entry.dias_gozados === '') {
                errors.push(
                  'Conta ' + conta + ' exige Dias Gozados na linha ' + (index + 2)
                );
              }
              // Dias Vendidos em branco é zero, não erro: vender dias é exceção.
              if (entry.dias_vendidos === null || entry.dias_vendidos === undefined || entry.dias_vendidos === '') {
                entry.dias_vendidos = 0;
              }
            }

            if (conta === CONTA_DECIMO && !temColunaParcela) {
              errors.push(
                'A linha ' + (index + 2) +
                  ' é de 13º, mas a planilha não tem a coluna Parcela. Baixe o modelo atualizado.'
              );
            } else if (conta === CONTA_DECIMO) {
              if (!entry.parcela) {
                errors.push(
                  'Conta ' + conta +
                    ' exige a Parcela (Adiantamento, Saldo ou Rescisório) na linha ' + (index + 2)
                );
              } else if (
                PARCELAS_DECIMO.map(normalizarTexto).indexOf(
                  normalizarTexto(entry.parcela)
                ) < 0
              ) {
                errors.push(
                  'Parcela inválida na linha ' + (index + 2) +
                    '. Use Adiantamento, Saldo ou Rescisório.'
                );
              }
            }

            // Coluna preenchida onde não se aplica é ignorada pelo sistema.
            // Sem avisar, quem preencheu supõe que foi considerado — e o erro
            // só aparece semanas depois, quando o número não bate.
            if (conta !== CONTA_DECIMO && entry.parcela) {
              errors.push(
                'A coluna Parcela não se aplica à conta ' + conta +
                  ' na linha ' + (index + 2) + '. Deixe em branco.'
              );
            }

            if (
              CONTAS_FERIAS.indexOf(conta) < 0 &&
              (entry.periodo_aquisitivo_inicio ||
                entry.periodo_aquisitivo_fim ||
                entry.dias_gozados ||
                entry.dias_vendidos)
            ) {
              errors.push(
                'As colunas de férias não se aplicam à conta ' + conta +
                  ' na linha ' + (index + 2) + '. Deixe em branco.'
              );
            }

            ret.push(entry);
          }
        });

        input.value = null;

        if (errors.length > 0) {
          // Nada é lançado: a planilha só entra inteira e sem erro.
          input.value = '';
          ref.setState({ errosImportacao: errors });
        } else if (ret.length === 0) {
          toast.error('A planilha não tem nenhuma linha preenchida.');
        } else {
          this.processarPagamentoBatch(JSON.stringify(ret));
        }
        })
        // Sem este catch, qualquer falha de leitura do arquivo virava uma
        // promise rejeitada que só aparecia no console do navegador: para quem
        // está usando, o clique simplesmente não fazia nada.
        .catch((erro) => {
          input.value = '';
          toast.error(
            'Não foi possível ler a planilha: ' +
              ((erro && erro.message) || 'arquivo inválido') +
              '. Baixe o modelo atualizado em "Baixar Template".'
          );
        });
      })
      .catch((erro) => {
        input.value = '';
        toast.error(
          'Não foi possível abrir a planilha: ' +
            ((erro && erro.message) || 'arquivo inválido') +
            '. Verifique se o arquivo é o modelo atual e não está corrompido.'
        );
      });
    });
  }

  _handleCheck = (name) => (event) => {
    var value =
      event.target && event.target.checked
        ? event.target.checked
        : typeof event === 'string'
          ? event
          : '';

    const nextState = { [name]: value };

    if (name === 'custoDiretoFilter' && value) {
      nextState.statusFilter = 'Pago';
      nextState.naturezaFilter = 'despesa';
    }

    this.setState(nextState, () => {
      this.setState({
        pagRebList: this.state.backupPagRebList.filter((lancamento) => {
          return this.filterTableData(lancamento, this.state.status);
        }),
      });
    });
  };

  processarPagamentoBatch = (dados) => {
    // metodo post para o servidor php, servidor retorna arquivo de erro

    AppState.isLoading = true;

    fetch(
      `${AppState.serverAddr}/lancamentos/AddPgtoRecbBatch.php`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json; charset=utf-8',
          Authorization: AppState.token,
        },
        body: dados,
      }
    )
      .then((response) => {
        if (!response.ok) {
          return response.json().then((json) => {
            var error = new Error(response.status);
            error.response = json;
            throw error;
          });
        }

        const res = response.json();
        return res;
      })
      .then((response) => {
        AppState.isLoading = false;
        toast.success('Importação realizada com sucesso!');
        this.getTableData();
      })
      .catch(function (error) {
        AppState.isLoading = false;
        if (error.toString() === '' || error.toString().split(' ').length < 2) {
          toast.error('Erro desconhecido, tente novamente');
          return;
        }
        var Err_status = error.toString().split(' ')[1];
        if (Err_status === '400') {
          const [ret, ...message] = error.response['status'].value.split(' ');
          if (ret === '40001') {
            console.log('ERRO dados inválidos');
            toast.error('Dados inválidos');
          } else if (ret === '40002') {
            toast.error('Erro: Planilha sem dados');
          } else if (ret === '40003') {
            toast.error(
              'Erro ao realizar lançamentos.\nVerifique os dados e tente novamente.'
            );
          } else if (ret === '40005' || ret === '40004') {
            toast.error(`Erro ao realizar lançamentos.\n${message.join(' ')}`);
          } else {
            toast.error('Dados inválidos');
          }
        } else if (Err_status === '401') {
          browserHistory.push(`${process.env.PUBLIC_URL}/logout`);
        } else if (Err_status === '500') {
          toast.error('Erro desconhecido, tente novamente');
        }
      });
  };

  getUsuarios = () => {
    fetch(
      `${AppState.serverAddr}/usuarios/GetUsers.php`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json; charset=utf-8',
          Authorization: AppState.token,
        },
      }
    )
      .then((response) => {
        if (!response.ok) {
          return response.json().then((json) => {
            let error = new Error(response.status);
            error.response = json;
            throw error;
          });
        } else {
          const res = response.json();
          return res;
        }
      })
      .then((response) => {
        this.setState({
          employeeList: response.request,
        });
      })
      .catch(function (error) {
        AppState.isLoading = false;
        if (error.toString() === '' || error.toString().split(' ').length < 2) {
          toast.error('Erro desconhecido, tente novamente');
          return;
        }
        var Err_status = error.toString().split(' ')[1];
        if (Err_status === '400') {
          var ret = error.response['status'].value.split(' ')[0];
          if (ret === '40001') {
            console.log('40001 Erro desconhecido');
          } else toast.error('Dados inválidos');
        } else if (Err_status === '401') {
          browserHistory.push(`${process.env.PUBLIC_URL}/logout`);
        } else if (Err_status === '500') {
          toast.error('Erro desconhecido, tente novamente');
        }
      });
  };

  getAllUsuarios = () => {
    fetch(
      `${AppState.serverAddr}/usuarios/GetUsers.php`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json; charset=utf-8',
          Authorization: AppState.token,
        },
      }
    )
      .then((response) => {
        if (!response.ok) {
          return response.json().then((json) => {
            let error = new Error(response.status);
            error.response = json;
            throw error;
          });
        } else {
          const res = response.json();
          return res;
        }
      })
      .then((response) => {
        this.setState({
          fullEmployeeList: response.request,
        });
      })
      .catch(function (error) {
        AppState.isLoading = false;
        if (error.toString() === '' || error.toString().split(' ').length < 2) {
          toast.error('Erro desconhecido, tente novamente');
          return;
        }
        var Err_status = error.toString().split(' ')[1];
        if (Err_status === '400') {
          var ret = error.response['status'].value.split(' ')[0];
          if (ret === '40001') {
            console.log('40001 Erro desconhecido');
          } else toast.error('Dados inválidos');
        } else if (Err_status === '401') {
          browserHistory.push(`${process.env.PUBLIC_URL}/logout`);
        } else if (Err_status === '500') {
          toast.error('Erro desconhecido, tente novamente');
        }
      });
  };

  getActiveProjects = () => {
    fetch(
      `${AppState.serverAddr}/projetos/GetProjects.php`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json; charset=utf-8',
          Authorization: AppState.token,
        },
      }
    )
      .then((response) => {
        if (!response.ok) {
          return response.json().then((json) => {
            let error = new Error(response.status);
            error.response = json;
            throw error;
          });
        } else {
          const res = response.json();
          return res;
        }
      })
      .then((response) => {
        this.setState({
          activeProjectList: response.request.map((item) => ({
            label: `${item.id_projeto} - ${item.desc_curta}`,
            value: `${item.id_projeto} - ${item.desc_curta}`,
          })),
        });
      })
      .catch(function (error) {
        if (error.toString() === '' || error.toString().split(' ').length < 2) {
          console.log('Erro ao buscar projetos ativos');
          return;
        }
        var Err_status = error.toString().split(' ')[1];
        if (Err_status === '401') {
          browserHistory.push(`${process.env.PUBLIC_URL}/logout`);
        }
      });
  };

  preparaDado = (dados) => {
    if (dados === null || dados.length < 1) {
      return [];
    }

    return dados.slice(0).map(({ _original }) => {
      return {
        ..._original,
        dt_vencimento: Utils.dateToPT(_original.dt_vencimento),
        dt_pag_recb: Utils.dateToPT(_original.dt_pag_recb),
        eh_corrente: _original.eh_corrente === 'f' ? 'Não' : 'Sim',
        valor: Utils.float2moeda(_original.valor),
        custo_indireto: _original.custo_indireto === 'f' ? 'Não' : 'Sim',
      };
    });
  };

  closeDialog = () => {
    this.setState({ dialog: false }, () => this.getTableData());
  };

  periodHandleChange = (event) => {
    this.setState(
      {
        dt_incio: '',
        dt_fim: '',
        period: event.target.value,
      },
      () => {
        if (this.state.period !== 'custom') {
          this.getTableData();
        }
      }
    );
  };

  statusHandleChange = (event) => {
    let status = event.target.value;
    this.setState({
      pagRebList: this.state.backupPagRebList.filter((lancamento) =>
        this.filterTableData(lancamento, status)
      ),
      statusFilter: status,
    });
  };

  naturezaHandleChange = (event) => {
    let natureza = event.target.value;
    this.setState({
      naturezaFilter: natureza,
    }, () => {
      this.setState({
        pagRebList: this.state.backupPagRebList.filter((lancamento) =>
          this.filterTableData(lancamento)
        ),
      });
    });
  };

  tipoContraparteHandleChange = (event) => {
    let tipoContraparte = event.target.value;
    this.setState({
      tipoContraparteFilter: tipoContraparte,
    }, () => {
      this.setState({
        pagRebList: this.state.backupPagRebList.filter((lancamento) =>
          this.filterTableData(lancamento)
        ),
      });
    });
  };

  projetoHandleChange = (event) => {
    let projetos = event.target.value;
    const anteriorTinhaAll = this.state.projetoFilter.includes('all');
    const anteriorTinhaAllButZero = this.state.projetoFilter.includes('allButZero');

    // Se selecionou "all", limpa todas as outras seleções
    if (projetos.includes('all') && !anteriorTinhaAll) {
      projetos = ['all'];
    }
    // Se selecionou "allButZero", limpa todas as outras seleções (é exclusivo)
    else if (projetos.includes('allButZero') && !anteriorTinhaAllButZero) {
      projetos = ['allButZero'];
    }
    // Se tinha "all" e selecionou outra coisa, remove o "all"
    else if (projetos.includes('all') && projetos.length > 1) {
      projetos = projetos.filter(p => p !== 'all');
    }
    // Se tinha "allButZero" e selecionou outra coisa, remove o "allButZero"
    else if (projetos.includes('allButZero') && projetos.length > 1) {
      projetos = projetos.filter(p => p !== 'allButZero');
    }
    // Se não tem nenhuma seleção, volta para "all"
    if (projetos.length === 0) {
      projetos = ['all'];
    }

    this.setState({
      projetoFilter: projetos,
    }, () => {
      this.setState({
        pagRebList: this.state.backupPagRebList.filter((lancamento) =>
          this.filterTableData(lancamento)
        ),
      });
    });
  };

  filterTableData = (lancamento, status = null) => {
    if (
      this.state.rateioFilter &&
      String(lancamento.descricao || '').includes('- rateio')
    ) {
      return false;
    }

    if(this.state.ocultarContas4Filter) {
      const idConta = String(lancamento.id_conta || '');
      if (idConta.startsWith('4.')) {
        return false;
      }
    }

    if (this.state.custoDiretoFilter) {
      if (lancamento.status !== 'Pago') {
        return false;
      }
      if (lancamento.natureza !== 'despesa') {
        return false;
      }
      if (lancamento.custo_indireto !== 'f') {
        return false;
      }
      const idConta = String(lancamento.id_conta || '');
      if (idConta.startsWith('3.') || idConta.startsWith('4.')) {
        return false;
      }
      if (
        localStorage.getItem('noInvoiceCurrent') === 'true' &&
        lancamento.eh_corrente !== 't'
      ) {
        return false;
      }
    }

    if (!this.state.custoDiretoFilter) {
      status = status == null ? this.state.statusFilter : status;
      if (status == 'Em aberto') {
        if (lancamento.status != 'Em Aprovação') return false;
      } else if (status == 'Aprovado') {
        if (lancamento.status != 'Aprovado') return false;
      } else if (status == 'Pago') {
        if (lancamento.status != 'Pago') return false;
      }

      // Filtro de natureza
      if (this.state.naturezaFilter !== 'Todos' && lancamento.natureza !== this.state.naturezaFilter) {
        return false;
      }
    }
    // Filtro de tipo contraparte específico para não mostrar lançamentos de nota de débito (conta 2.18) quando a opção "todos_nota_debito" estiver selecionada
    if (this.state.tipoContraparteFilter === 'todos_nota_debito') {
      if (String(lancamento.id_conta || '') === '2.18') {
        return false;
      }
    
    }
    // Filtro de tipo contraparte
    else if (this.state.tipoContraparteFilter !== 'Todos' && lancamento.tipo_contraparte !== this.state.tipoContraparteFilter) {
      return false;
    }

    // Filtro de projeto (múltipla seleção)
    const projetoFilter = this.state.projetoFilter;
    if (!projetoFilter.includes('all')) {
      // Se tem "allButZero" selecionado, exclui o projeto 0
      if (projetoFilter.includes('allButZero')) {
        if (lancamento.nome_display === '0 - AmbientalConsult') {
          return false;
        }
        // Se só tem allButZero, aceita todos os outros
        if (projetoFilter.length === 1) {
          return true;
        }
      }
      // Verifica se o projeto do lançamento está na lista de selecionados
      const projetosSelecionados = projetoFilter.filter(p => p !== 'allButZero');
      if (projetosSelecionados.length > 0 && !projetosSelecionados.includes(lancamento.nome_display)) {
        return false;
      }
    }

    return true;
  };

  periodTypeHandleChange = (event) => {
    this.setState(
      {
        periodType: event.target.value,
      },
      () => {
        this.getTableData();
      }
    );
  };

  getTableData = () => {
    AppState.isLoading = true;

    fetch(
      `${AppState.serverAddr}/lancamentos/GetPgtoRecebs.php?json={"periodo":"${this.state.period}","tipo_data":"${this.state.periodType}","dt_inicio": "${this.state.dt_inicio}","dt_fim": "${this.state.dt_fim}"}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json; charset=utf-8',
          Authorization: AppState.token,
        },
      }
    )
      .then((response) => {
        if (!response.ok) {
          return response.json().then((json) => {
            let error = new Error(response.status);
            error.response = json;
            throw error;
          });
        } else {
          AppState.isLoading = false;
          const res = response.json();
          return res;
        }
      })
      .then((response) => {
        const data = response.request.filter((lancamento) =>
          this.filterTableData(lancamento)
        );

        const uniqueProjects = Array.from(
          new Set(data.map((el) => el.nome_display))
        ).sort();

        this.setState(
          {
            pagRebList: data,
            backupPagRebList: response.request,
            projectList: uniqueProjects.map((el) => ({ label: el, value: el })),
          },
          () => {
            AppState.isLoading = false;
          }
        );
      })
      .catch(function (error) {
        AppState.isLoading = false;
        if (error.toString() === '' || error.toString().split(' ').length < 2) {
          toast.error('Erro desconhecido, tente novamente');
          return;
        }
        var Err_status = error.toString().split(' ')[1];
        if (Err_status === '400') {
          var ret = error.response['status'].value.split(' ')[0];
          if (ret === '40001' || ret === '40002') {
            console.log('Dados invalidos/não informados');
          }

          if (ret === '40003') {
            console.log('Erro desconhecido');
          } else toast.error('Dados inválidos');
        } else if (Err_status === '401') {
          browserHistory.push(`${process.env.PUBLIC_URL}/logout`);
        } else if (Err_status === '500') {
          toast.error('Erro desconhecido, tente novamente');
        }
      });
  };

  dateFilterHandleChange = (name) => (event) => {
    this.setState(
      {
        [name]: event.target.value,
      },
      () => {
        if (
          this.state.dt_inicio.length === 10 &&
          this.state.dt_fim.length === 10
        )
          this._debouncedHandleChange();
      }
    );
  };

  closeDialog = () => {
    this.setState({ dialog: false, edicao: false }, () => this.getTableData());
  };

  openDialog = () => {
    this.setState({
      edicao: false,
      dialog: true,
    });
  };

  openDialogEdicao = (idPagamento) => {
    this.setState({
      edicao: true,
      dialog: true,
      pagamento: idPagamento,
    });
  };

  confirmRateio = () => {
    browserHistory.push(`${process.env.PUBLIC_URL}/confirm-rateio`);
  };

  displayDeleteConfirmation = (lancamento) => {
    this.setState({
      dialogConfirmationDelete: true,
      lancamentoSelecionado: lancamento,
    });
  };

  onConfirmDelete = (idLancamento, isSelected) => {
    AppState.isLoading = true;
    lancamentoAPi
      .deleteLancamento(idLancamento, isSelected)
      .then(() => {
        AppState.isLoading = false;
        toast.error('Pgto/Receb removido com sucesso.');
        this.getTableData();
      })
      .catch(function (error) {
        AppState.isLoading = false;
        if (error.toString() === '' || error.toString().split(' ').length < 2) {
          toast.error('Erro desconhecido, tente novamente');
          return;
        }
        var Err_status = error.toString().split(' ')[1];
        if (Err_status === '400') {
          var ret = error.response['status'].value.split(' ')[0];
          if (ret === '40001') {
            console.log('ERRO dados inválidos');
            toast.error('Dados inválidos');
          } else if (ret === '40002') {
            toast.error('Erro: Não foi possível remover o lançamento');
          } else if (ret === '40003') {
            toast.error(
              'Erro: Não foi possível remover o título vinculado ao lançamento'
            );
          } else {
            toast.error('Dados inválidos');
          }
        } else if (Err_status === '401') {
          browserHistory.push(`${process.env.PUBLIC_URL}/logout`);
        } else if (Err_status === '500') {
          toast.error('Erro desconhecido, tente novamente');
        }
      })
      .finally(() => {
        this.setState({
          lancamentoSelecionado: '',
          dialogConfirmationDelete: false,
        });
      });
  };

  render() {
    const hasDeletePermission = this.hasDeletePermission();
    const hasEditPermission = this.hasEditPermission();

    const actions = (
      <>
        <a
          href={`${AppState.serverAddr}/arquivos/Template_lancamento_pag_rec.xlsx?v=${Date.now()}`}
          download
        >
          Baixar Template
        </a>

        <DsButton
          intent="outlined"
          component="span"
          style={{ height: '36px' }}
          onClick={() => this.confirmRateio()}
        >
          APROVAR RATEIO
        </DsButton>

        <label
          htmlFor="raised-button-file"
          style={{ marginBottom: 0 }}
        >
          <input
            accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
            id="raised-button-file"
            type="file"
            ref={this.importInput}
            style={{ display: 'none' }}
          />
          <DsButton
            intent="outlined"
            component="span"
            style={{ height: '36px' }}
          >
            Importar Folha Pgto
          </DsButton>
        </label>
        <DsButton
          intent="primary"
          onClick={() => this.openDialog()}
        >
          <AddIcon />
          Novo PGTO/RECEB
        </DsButton>
        <ExcelFile
          filename={'RelatorioPgtoRecebs-' + Utils.getTodayDate()}
          element={
            <DsButton
              intent="outlined"
              onClick={this.exportarExcel}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                x="0px"
                y="0px"
                width="24"
                height="24"
                viewBox="0 0 192 192"
                style={{ fill: '#1a1a1a', paddingRight: '6px' }}
              >
                <g
                  fill="none"
                  fillRule="nonzero"
                  stroke="none"
                  strokeWidth="1"
                  strokeLinecap="butt"
                  strokeLinejoin="miter"
                  strokeMiterlimit="10"
                  strokeDasharray=""
                  strokeDashoffset="0"
                  fontFamily="none"
                  fontWeight="none"
                  fontSize="none"
                  textAnchor="none"
                  style={{ mixBlendMode: 'normal' }}
                >
                  <path d="M0,192v-192h192v192z" fill="none"></path>
                  <g fill="#1a1a1a">
                    <g id="surface1">
                      <path d="M110.88,0c-0.075,0.03 -0.165,0.075 -0.24,0.12l-107.52,20.4c-1.83,0.345 -3.15,1.965 -3.12,3.84v143.28c-0.03,1.875 1.29,3.495 3.12,3.84l107.52,20.4c1.11,0.21 2.265,-0.075 3.15,-0.795c0.87,-0.72 1.395,-1.785 1.41,-2.925v-19.2h65.28c4.2,0 7.68,-3.48 7.68,-7.68v-130.56c0,-4.2 -3.48,-7.68 -7.68,-7.68h-65.28v-19.2c0.015,-1.11 -0.465,-2.16 -1.29,-2.895c-0.825,-0.735 -1.935,-1.08 -3.03,-0.945zM107.52,8.4v16.68c-0.51,1.065 -0.51,2.295 0,3.36v135.96c-0.105,0.51 -0.105,1.05 0,1.56v17.64l-99.84,-19.08v-137.04zM115.2,30.72h65.28v130.56h-65.28v-19.2h15.36v-7.68h-15.36v-23.04h15.36v-7.68h-15.36v-19.2h15.36v-7.68h-15.36v-19.2h15.36v-7.68h-15.36zM138.24,49.92v7.68h30.72v-7.68zM25.68,60.24l21,35.88l-22.92,35.88h19.2l12.48,-23.16c0.87,-2.235 1.44,-3.945 1.68,-5.04h0.12c0.495,2.34 0.975,3.93 1.44,4.8l12.48,23.4h19.08l-22.08,-36.24l21.48,-35.52h-18l-11.4,21.24c-1.095,2.775 -1.875,4.965 -2.28,6.36h-0.12c-0.63,-2.34 -1.35,-4.425 -2.16,-6.12l-10.32,-21.48zM138.24,76.8v7.68h30.72v-7.68zM138.24,103.68v7.68h30.72v-7.68zM138.24,134.4v7.68h30.72v-7.68z"></path>
                    </g>
                  </g>
                </g>
              </svg>
              Exportar
            </DsButton>
          }
        >
          <ExcelSheet
            data={() =>
              this.preparaDado(
                this.selectTable.getResolvedState().sortedData
              )
            }
            name="PgtoReceb"
          >
            <ExcelColumn
              label="ID Pgto/Recb"
              value="id_lancamento"
              style={{ font: { sz: '24', bold: true } }}
            />
            <ExcelColumn
              label="ID Título"
              value="id_titulo"
              style={{ font: { sz: '24', bold: true } }}
            />
            {localStorage.getItem('noInvoiceCurrent') === 'false' ? (
              <ExcelColumn
                label="É Corrente?"
                value="eh_corrente"
                style={{ font: { sz: '24', bold: true } }}
              />
            ) : []}
            <ExcelColumn
              label="Tipo Contraparte"
              value="tipo_contraparte"
              style={{ font: { sz: '24', bold: true } }}
            />
            <ExcelColumn
              label="CPJ/CNPJ Contraparte"
              value="cpf_cnpj_contraparte"
              style={{ font: { sz: '24', bold: true } }}
            />
            <ExcelColumn
              label="Nome Contraparte"
              value="nome_contraparte"
              style={{ font: { sz: '24', bold: true } }}
            />
            <ExcelColumn
              label="Natureza"
              value="natureza"
              style={{ font: { sz: '24', bold: true } }}
            />
            <ExcelColumn
              label="Conta"
              value="id_conta"
              style={{ font: { sz: '24', bold: true } }}
            />
            <ExcelColumn
              label="Desc. Conta"
              value="desc_conta"
              style={{ font: { sz: '24', bold: true } }}
            />
            <ExcelColumn
              label="Projeto"
              value="nome_display"
              style={{ font: { sz: '24', bold: true } }}
            />
            <ExcelColumn
              label="Aprovador"
              value="aprovador_nome"
              style={{ font: { sz: '24', bold: true } }}
            />
            <ExcelColumn
              label="Pagador"
              value="nome_pagador"
              style={{ font: { sz: '24', bold: true } }}
            />
            <ExcelColumn
              label="Descrição"
              value="descricao"
              style={{ font: { sz: '24', bold: true } }}
            />
            <ExcelColumn
              label="Valor"
              value="valor"
              style={{ font: { sz: '24', bold: true } }}
            />
            <ExcelColumn
              label="Data Pgto./Receb."
              value="dt_pag_recb"
              style={{ font: { sz: '24', bold: true } }}
            />
            <ExcelColumn
              label="Data Vencimento"
              value="dt_vencimento"
              style={{ font: { sz: '24', bold: true } }}
            />
            <ExcelColumn
              label="Forma Pagamento"
              value="forma_pagamento"
              style={{ font: { sz: '24', bold: true } }}
            />
            <ExcelColumn
              label="Contraparte Outro"
              value="nome_contraparte_outro"
              style={{ font: { sz: '24', bold: true } }}
            />
            <ExcelColumn
              label="Código Contrato"
              value="id_contrato"
              style={{ font: { sz: '24', bold: true } }}
            />
            <ExcelColumn
              label="N Cheque"
              value="n_cheque"
              style={{ font: { sz: '24', bold: true } }}
            />
            <ExcelColumn
              label="Banco Pgto/Receb"
              value="banco_pgto_receb"
              style={{ font: { sz: '24', bold: true } }}
            />
            <ExcelColumn
              label="N Documento"
              value="num_documento"
              style={{ font: { sz: '24', bold: true } }}
            />
            <ExcelColumn
              label="Custo Indireto"
              value="custo_indireto"
              style={{ font: { sz: '24', bold: true } }}
            />
          </ExcelSheet>
        </ExcelFile>

        <Dialog
          open={this.state.errosImportacao.length > 0}
          onClose={() => this.setState({ errosImportacao: [] })}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Planilha não importada</DialogTitle>
          <DialogContent>
            <p style={{ marginTop: 0 }}>
              Nenhum lançamento foi feito. Corrija{' '}
              {this.state.errosImportacao.length === 1
                ? 'o problema abaixo'
                : `os ${this.state.errosImportacao.length} problemas abaixo`}{' '}
              e importe de novo:
            </p>
            <ul style={{ paddingLeft: 20, margin: 0, maxHeight: 360, overflowY: 'auto' }}>
              {this.state.errosImportacao.map((erro, i) => (
                <li key={i} style={{ marginBottom: 4 }}>{erro}</li>
              ))}
            </ul>
          </DialogContent>
          <DialogActions>
            <DsButton intent="primary" onClick={() => this.setState({ errosImportacao: [] })}>
              Fechar
            </DsButton>
          </DialogActions>
        </Dialog>
      </>
    );

    const filters = (
      <>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Checkbox
              checked={this.state.rateioFilter}
              onChange={this._handleCheck('rateioFilter')}
              style={{ padding: '0 8px 0 0' }}
            />
            <span>Apaga rateios</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Checkbox
              checked={this.state.custoDiretoFilter}
              onChange={this._handleCheck('custoDiretoFilter')}
              style={{ padding: '0 8px 0 0' }}
            />
            <span>Filtra custo direto</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Checkbox
              checked={this.state.ocultarContas4Filter}
              onChange={this._handleCheck('ocultarContas4Filter')}
              style={{ padding: '0 8px 0 0' }}
            />
            <span>Ocultar contas 4</span>
          </div>
        </div>
      </>
    );

    return (
      <div id={this.constructor.name} style={{ marginTop: '5em' }}>
        <PageTitle>Pagamentos / Recebimentos</PageTitle>

        <Panel style={{ marginBottom: 16 }}>
          {filters}

        <Grid
          container
          spacing={16}
          style={{ marginTop: '16px', marginBottom: '0px' }}
        >
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel htmlFor={'period'}>Tipo de Filtro</InputLabel>
              <Select
                inputProps={{ id: 'period', name: 'period' }}
                value={this.state.periodType}
                onChange={this.periodTypeHandleChange}
              >
                <MenuItem value={'dt_pag_recb'}>Pagamento/Receb.</MenuItem>
                <MenuItem value={'dt_vencimento'}>Vencimento</MenuItem>
              </Select>
            </FormControl>
          </Grid>


          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                inputProps={{ id: 'status', name: 'status' }}
                value={this.state.statusFilter}
                onChange={this.statusHandleChange}
              >
                <MenuItem value={'Todos'}>Todos</MenuItem>
                <MenuItem value={'Aprovado'}>Em aberto (Aprovado)</MenuItem>
                <MenuItem value={'Pago'}>Pago</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>Natureza</InputLabel>
              <Select
                inputProps={{ id: 'natureza', name: 'natureza' }}
                value={this.state.naturezaFilter}
                onChange={this.naturezaHandleChange}
              >
                <MenuItem value={'Todos'}>Todos</MenuItem>
                <MenuItem value={'receita'}>Receita</MenuItem>
                <MenuItem value={'despesa'}>Despesa</MenuItem>
                <MenuItem value={'controle'}>Controle</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>Tipo Contraparte</InputLabel>
              <Select
                inputProps={{ id: 'tipoContraparte', name: 'tipoContraparte' }}
                value={this.state.tipoContraparteFilter}
                onChange={this.tipoContraparteHandleChange}
              >
                <MenuItem value={'Todos'}>Todos</MenuItem>
                <MenuItem value={'todos_nota_debito'}>Todos Menos Nota de Débito</MenuItem>
                <MenuItem value={'cliente'}>Cliente</MenuItem>
                <MenuItem value={'fornecedor'}>Fornecedor</MenuItem>
                <MenuItem value={'funcionario'}>Funcionário</MenuItem>
                <MenuItem value={'outros'}>Outros</MenuItem>
                <MenuItem value={'pj'}>PJ</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>Projeto</InputLabel>
              <Select
                multiple
                inputProps={{ id: 'projeto', name: 'projeto' }}
                value={this.state.projetoFilter}
                onChange={this.projetoHandleChange}
                renderValue={(selected) => {
                  if (selected.includes('all')) return 'Todos';
                  if (selected.includes('allButZero') && selected.length === 1) return 'Todos Menos 0';
                  return selected.filter(s => s !== 'allButZero').join(', ');
                }}
              >
                <MenuItem value={'all'}>Todos</MenuItem>
                <MenuItem value={'allButZero'}>Todos Menos 0</MenuItem>
                <MenuItem value={'0 - AmbientalConsult'}>0 - AmbientalConsult</MenuItem>
                {this.state.activeProjectList
                  .filter(el => el.value !== '0 - AmbientalConsult')
                  .map((el) => (
                    <MenuItem key={el.value} value={el.value}>{el.label}</MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>
                Período de{' '}
                {this.state.periodType == 'dt_pag_recb'
                  ? 'Lançamento'
                  : 'Vencimento'}
              </InputLabel>
              <Select
                inputProps={{ id: 'period', name: 'period' }}
                value={this.state.period}
                onChange={this.periodHandleChange}
              >
                <MenuItem value={'Hoje'}>Hoje</MenuItem>
                <MenuItem value={'Ontem'}>Ontem</MenuItem>
                <MenuItem value={'5dias'}>Últimos 5 dias</MenuItem>
                <MenuItem value={'EsseMes'}>Esse Mês</MenuItem>
                <MenuItem value={'30dias'}>Últimos 30 dias</MenuItem>
                <MenuItem value={'60dias'}>Últimos 60 dias</MenuItem>
                <MenuItem value={'90dias'}>Últimos 90 dias</MenuItem>
                <MenuItem value={'p5dias'}>Próximos 5 dias</MenuItem>
                <MenuItem value={'custom'}>Customizado</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {this.state.period === 'custom' && (
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Inicio"
                type="date"
                defaultValue={this.state.dt_inicio}
                onChange={this.dateFilterHandleChange('dt_inicio')}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          )}

          {this.state.period === 'custom' && (
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Fim"
                type="date"
                defaultValue={this.state.dt_fim}
                onChange={this.dateFilterHandleChange('dt_fim')}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          )}
        </Grid>
        </Panel>

        <DataTable
          actions={actions}
          filterable={true}
          columnFilters
          data={this.state.pagRebList}
          defaultPageSize={20}
          ref={(r) => {
            this.selectTable = r;
          }}
          filterMethod={(filter, row) => {
            const filterValue = filter.value.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
            const cellValue = String(row[filter.id] || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
            
            if (filter.id === 'dt_pag_recb' || filter.id === 'dt_vencimento') {
              return Utils.dateToPT(String(row[filter.id])).includes(filterValue);
            }
            
            return cellValue.includes(filterValue);
          }}
          columns={[
                {
                  Header: 'Nº',
                  accessor: 'id_lancamento',
                  width: 60,
                  Cell: (row) => (
                    <div
                      style={{
                        height: '22px',
                        textAlign: 'center',
                        verticalAlign: 'middle',
                        position: 'relative',
                        top: '15%',
                      }}
                    >
                      {row.value}
                    </div>
                  ),
                },
                {
                  Header: 'Titulo',
                  accessor: 'id_titulo',
                  width: 60,
                  Cell: (row) => (
                    <div
                      style={{
                        height: '22px',
                        verticalAlign: 'middle',
                        textAlign: 'center',
                        position: 'relative',
                        top: '15%',
                      }}
                    >
                      {row.value}
                    </div>
                  ),
                },
                // {
                //   Header: "Status",
                //   accessor: "status",
                //   width: 80,
                //   Cell: row => (
                //     <div
                //       style={{
                //         height: "22px",
                //         verticalAlign: "middle",
                //         position: "relative",
                //         top: "15%",
                //         textTransform:
                //           row.value === "pj" ? "uppercase" : "capitalize"
                //       }}
                //     >
                //       {row.value}
                //     </div>
                //   )
                // },
                {
                  Header: 'Nome Contraparte',
                  accessor: 'nome_contraparte',
                  Cell: (row) => (
                    <div
                      style={{
                        height: '22px',
                        textAlign: 'center',
                        verticalAlign: 'middle',
                        position: 'relative',
                        top: '15%',
                      }}
                    >
                      {row.value}
                    </div>
                  ),
                },
                {
                  Header: 'conta',
                  accessor: 'id_conta',
                  width: 60,
                  Cell: (row) => (
                    <div
                      style={{
                        height: '22px',
                        textAlign: 'center',
                        verticalAlign: 'middle',
                        position: 'relative',
                        top: '15%',
                      }}
                    >
                      {row.value}
                    </div>
                  ),
                },
                {
                  Header: 'Desc. Conta',
                  accessor: 'desc_conta',
                  Cell: (row) => (
                    <div
                      style={{
                        height: '22px',
                        textAlign: 'center',
                        verticalAlign: 'middle',
                        position: 'relative',
                        top: '15%',
                      }}
                    >
                      {row.value}
                    </div>
                  ),
                },
                {
                  Header: 'Descrição',
                  accessor: 'descricao',
                  Cell: (row) => (
                    <div
                      style={{
                        height: '22px',
                        textAlign: 'center',
                        verticalAlign: 'middle',
                        position: 'relative',
                        top: '15%',
                      }}
                    >
                      {row.value}
                    </div>
                  ),
                },
                {
                  Header: 'valor',
                  accessor: 'valor',
                  width: 90,
                  sortMethod: (a, b) => {
                    // Converte strings no formato brasileiro para número
                    const valorA = typeof a === 'string' 
                      ? parseFloat(a.replace(/\./g, '').replace(',', '.'))
                      : parseFloat(a);
                    const valorB = typeof b === 'string'
                      ? parseFloat(b.replace(/\./g, '').replace(',', '.'))
                      : parseFloat(b);
                    
                    return valorA - valorB;
                  },
                  Footer: (row) => (
                    <span>
                      {
                        // Get the total of the price
                        'R$ ' +
                        Utils.float2moeda(
                          row.data.reduce(
                            (total, { valor }) =>
                              (total += parseFloat(valor)),
                            0
                          )
                        )
                      }
                    </span>
                  ),
                  Cell: (row) => (
                    <div
                      style={{
                        height: '22px',
                        verticalAlign: 'middle',
                        position: 'relative',
                        top: '15%',
                      }}
                    >
                      {'R$ ' + Utils.float2moeda(row.value)}
                    </div>
                  ),
                },
                {
                  Header: 'Dia Pgto',
                  accessor: 'dt_pag_recb',
                  width: 95,
                  Cell: (row) => (
                    <div
                      style={{
                        height: '22px',
                        textAlign: 'center',
                        verticalAlign: 'middle',
                        position: 'relative',
                        top: '15%',
                      }}
                    >
                      {Utils.dateToPT(row.value)}
                    </div>
                  ),
                },
                {
                  Header: 'Vencimento',
                  accessor: 'dt_vencimento',
                  width: 90,
                  Cell: (row) => (
                    <div
                      style={{
                        height: '22px',
                        textAlign: 'center',
                        verticalAlign: 'middle',
                        position: 'relative',
                        top: '15%',
                      }}
                    >
                      {Utils.dateToPT(row.value)}
                    </div>
                  ),
                },
                {
                  Header: 'Ação',
                  width: 90,
                  Cell: (row) => (
                    <div
                      style={{
                        height: '22px',
                        textAlign: 'left',
                        verticalAlign: 'middle',
                        position: 'relative',
                      }}
                    >
                      <Grid
                        container
                        direction="row"
                        justify="space-around"
                        alignItems="baseline"
                      >
                        {hasDeletePermission ? (
                          <DeleteIcon
                            style={{ cursor: 'pointer' }}
                            onClick={(event) =>
                              this.displayDeleteConfirmation(row.original)
                            }
                          />
                        ) : (
                          ''
                        )}
                        {hasEditPermission ? (
                          <EditICon
                            onClick={() =>
                              this.openDialogEdicao(
                                row.original['id_lancamento']
                              )
                            }
                            style={{
                              cursor: 'pointer',
                              width: '22px',
                              height: '22px',
                              minWidth: '22px',
                              minHeight: '22px',
                              marginLeft: '5px',
                            }}
                          />
                        ) : (
                          ''
                        )}
                      </Grid>
                    </div>
                  ),
                },
          ]}
        />
        {this.state.dialogConfirmationDelete && (
          <Modal
            titulo="Confirmação"
            onCloseButton={() =>
              this.setState({
                dialogConfirmationDelete: false,
                id_lancamento_selecionado: '',
              })
            }
            isOpen={this.state.dialogConfirmationDelete}
          >
            <ConfirmDeleteForm
              lancamento={this.state.lancamentoSelecionado}
              onConfirm={this.onConfirmDelete}
            />
          </Modal>
        )}
        <Form
          open={this.state.dialog}
          close={this.closeDialog}
          pagamento={this.state.pagamento}
          edicao={this.state.edicao}
          isLoading={true}
        />
      </div>
    );
  }
}

export default Pagamentos;
